import { globalResizeObserver } from 'base/ui/measure';
import { ComponentStyle, NestedNode } from 'base/webflow/schema';
import { computed, makeObservable, observable } from 'mobx';
import React from 'react';

/**
 * Measuring the height of a component is not as simple as HTMLElement.clientHeight.
 *
 * Motivating cases:
 *
 * - a `position: absolute` dropdown extends downwards below the container element
 * - ditto, but upwards
 */
export class BoundsMeasurer {
  private measuring = new Map<NestedNode | 'container', HTMLElement>();
  private measurements = observable.map<
    NestedNode | 'container',
    Readonly<{ top: number; bottom: number }>
  >([], { deep: false, name: 'BoundsMeasurer::measurements' });
  private containerEl: HTMLElement | null = null;

  private readonly classIdsRequiringMeasurement = new Set<string>();
  private readonly elementsWithoutOverflow = new Set<HTMLElement>();
  private readonly stylesIngested = new WeakSet<readonly ComponentStyle[]>();

  constructor() {
    makeObservable(this);
  }

  /** Needs to be called whenever the component's styles change, before the nodes using the new styles are rendered */
  ingestStyles(styles: readonly ComponentStyle[]) {
    if (this.stylesIngested.has(styles)) return;
    this.stylesIngested.add(styles);
    for (const id of getClassIdsRequiringMeasurement(styles)) {
      this.classIdsRequiringMeasurement.add(id);
    }
  }

  /**
   * Vertical bounds of the container element only.
   *
   * Doesn't take into account things like absolutely positioned elements that
   * might extend beyond the container.
   */
  @computed.struct
  get containerBounds() {
    const m = this.measurements.get('container');

    if (!m) return { top: 0, bottom: 0, height: 0 };

    return {
      top: m.top,
      bottom: m.bottom,
      height: m.bottom - m.top,
    };
  }

  /**
   * Vertical bounds of all elements in the component, including those extending
   * beyond the container, e.g. dropdowns or modals.
   */
  @computed.struct
  get bounds() {
    let top = 0;
    let bottom = 0;
    for (const m of this.measurements.values()) {
      top = Math.min(top, m.top);
      bottom = Math.max(bottom, m.bottom);
    }
    return { top, bottom, height: bottom - top };
  }

  attach(
    node: NestedNode | 'container',
    elementRef: React.RefObject<HTMLElement>,
  ): (() => void) | undefined {
    if (node !== 'container' && !this.shouldAttach(node)) return;

    const el = elementRef.current;
    if (el == null) throw new Error('can not attach to unmounted element');
    if (node === 'container') this.containerEl = el;
    const style = getComputedStyle(el);
    if (stopsOverflow(style.overflowX) || stopsOverflow(style.overflowY)) {
      this.elementsWithoutOverflow.add(el);
    }

    this.refreshOnly(node, el);
    this.measuring.set(node, el);
    const dispose = globalResizeObserver.observe(el, this.refreshAll);
    return () => {
      dispose();
      this.measuring.delete(node);
      this.measurements.delete(node);
      this.elementsWithoutOverflow.delete(el);
    };
  }

  /**
   * If 1 node resizes, the rest might have moved vertically.
   *
   * This isn't too inefficient, as globalResizeObserver calls this once per
   * frame-batch maximum.
   **/
  private readonly refreshAll = () => {
    for (const [node, el] of this.measuring.entries()) {
      this.refreshOnly(node, el);
    }
  };

  private refreshOnly(node: NestedNode | 'container', el: HTMLElement) {
    if (!this.containerEl) return;

    for (const noOverflow of this.elementsWithoutOverflow) {
      if (noOverflow !== el && noOverflow.contains(el)) {
        // This element is hidden by an ancestor, so it doesn't affect the
        // height of the component.
        this.measurements.delete(node);
        return;
      }
    }

    let top = 0;
    let bottom = el.clientHeight;
    // Precondition: el is inside or equal to containerEl
    let at = el;
    // Assumption: containerEl is an offsetParent.  That's the case with
    // ComponentView since it sets a transform.
    while (at !== this.containerEl) {
      if (!(at.offsetParent instanceof HTMLElement)) {
        const cs = getComputedStyle(at);
        if (cs.position === 'fixed') {
          // Don't treat fixed elements as 0 height.  (e.g. modal signup forms)
          break;
        }
        // This could happen either when:
        //   1 - the above assumption is broken (oh no)
        //   2 - the element is inside a `display: none` container
        // Handle the 2nd case only:
        top = 0;
        bottom = 0;
        break;
      }
      // NOTE: this _should_ apply the transformation of the parent to the
      // height value.  As that isn't used in Relume Library components, it
      // is not implemented for now.
      top += at.offsetTop;
      bottom += at.offsetTop;
      at = at.offsetParent;
    }

    const height = bottom - top;
    if (height < 1e-6) {
      // Sometimes there are 0 height elements outside the container,
      // e.g. Layout 356's anchor element.
      this.measurements.delete(node);
      return;
    }

    this.measurements.set(node, { top, bottom });
  }

  private shouldAttach(node: NestedNode) {
    if (node.text) return false;
    return (
      node.classes.some((id) => this.classIdsRequiringMeasurement.has(id)) ||
      // gets `position: absolute` from w-dropdown-list
      node.type === 'DropdownList'
    );
  }
}

function getClassIdsRequiringMeasurement(styles: readonly ComponentStyle[]) {
  return new Set(
    styles
      .filter((s) => {
        return (
          requiresMeasurement(s.styleLess) ||
          Object.values(s.variants).some((variant) =>
            requiresMeasurement(variant.styleLess),
          )
        );
      })
      .map((s) => s._id),
  );
}

function requiresMeasurement(less: string) {
  return (
    less.match(/position:\s*(relative|absolute|fixed|sticky);/) ||
    less.match(/overflow(|-x|-y):/)
  );
}

/** Which overflow values hide the child elements */
function stopsOverflow(overflow: string) {
  return (
    overflow === 'hidden' ||
    overflow === 'scroll' ||
    overflow === 'auto' ||
    overflow === 'clip' ||
    overflow === 'overlay'
  );
}
